import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogProps,
  FormLabel,
  InputGroup,
  InputMessage,
  InputText,
  Stack,
  Text,
} from "@aksara-ui/react";
import { Field, FieldProps, Form, Formik, FormikValues } from "formik";
import React, { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { storeQuestUnit, updateQuestUnit } from "redux/actions/quest";
import styled from "styled-components";
import * as yup from "yup";

const FormWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

interface Props {
  unitId: string;
}

const initialValues = {
  name: "",
  slug: "",
  description: "",
  is_active: true,
};

const UnitFormModal: React.FC<Props & DialogProps> = ({
  isOpen,
  onClose,
  unitId,
}) => {
  const isAddMode = !unitId;
  const dispatch = useDispatch();
  const formikRef = useRef<FormikValues>() as any;

  const { questUnits, isFormLoading } = useSelector(
    (state: any) => state.questReducer
  );

  const handleCloseModal = () => {
    onClose && onClose();
  };

  const handleSubmit = (values: any) => {
    isAddMode
      ? dispatch(storeQuestUnit(values, handleCloseModal) as any)
      : dispatch(updateQuestUnit(unitId, values, handleCloseModal) as any);
  };

  useEffect(() => {
    if (!isAddMode && isOpen) {
      const detail = questUnits.find(
        (item: any) => String(item.id) === String(unitId)
      );
      if (detail && formikRef.current) {
        formikRef.current.setValues({
          name: detail.name ?? "",
          slug: detail.slug ?? "",
          description: detail.description ?? "",
          is_active: detail.is_active ?? true,
        });
      }
    } else if (isAddMode && formikRef.current) {
      formikRef.current.resetForm();
    }
  }, [isAddMode, questUnits, unitId, isOpen]);

  const validationSchema = yup.object({
    name: yup.string().required("Name wajib diisi"),
    slug: yup.string().required("Slug wajib diisi"),
  });

  return (
    <Dialog
      disableOverlayClick
      isOpen={isOpen}
      onClose={() => handleCloseModal()}
      overflowY={"auto"}
    >
      <Formik
        innerRef={formikRef}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {() => (
          <Form>
            <DialogHeader>
              <Text fontWeight={700}>
                {isAddMode ? "Tambah Unit" : "Edit Unit"}
              </Text>
            </DialogHeader>
            <DialogContent>
              <FormWrapper>
                <Field name="name">
                  {({ field, meta }: FieldProps<string>) => (
                    <Stack spacing="xs">
                      <FormLabel htmlFor="name">Nama Unit</FormLabel>
                      <InputGroup inputSize={40}>
                        <InputText
                          type="text"
                          size={40}
                          errors={!!meta.error && meta.touched}
                          {...field}
                        />
                      </InputGroup>
                      {!!meta.error && meta.touched && (
                        <InputMessage variant="error">{meta.error}</InputMessage>
                      )}
                    </Stack>
                  )}
                </Field>
                <Field name="slug">
                  {({ field, meta }: FieldProps<string>) => (
                    <Stack spacing="xs">
                      <FormLabel htmlFor="slug">Slug</FormLabel>
                      <InputGroup inputSize={40}>
                        <InputText
                          type="text"
                          size={40}
                          errors={!!meta.error && meta.touched}
                          placeholder="contoh: dufan"
                          {...field}
                        />
                      </InputGroup>
                      {!!meta.error && meta.touched && (
                        <InputMessage variant="error">{meta.error}</InputMessage>
                      )}
                    </Stack>
                  )}
                </Field>
                <Field name="description">
                  {({ field, meta }: FieldProps<string>) => (
                    <Stack spacing="xs">
                      <FormLabel htmlFor="description">Deskripsi</FormLabel>
                      <InputGroup inputSize={40}>
                        <InputText
                          type="text"
                          size={40}
                          errors={!!meta.error && meta.touched}
                          {...field}
                        />
                      </InputGroup>
                    </Stack>
                  )}
                </Field>
              </FormWrapper>
            </DialogContent>
            <DialogFooter>
              <Stack
                display="flex"
                flexDirection="row"
                spacing="xs"
                flex={1}
                style={{ gap: 15 }}
              >
                <Button
                  variant="outline"
                  block
                  type="button"
                  onClick={() => handleCloseModal()}
                >
                  Batal
                </Button>
                <Button
                  variant="primary"
                  block
                  type="submit"
                  isLoading={isFormLoading}
                >
                  {isAddMode ? "Simpan" : "Perbarui"}
                </Button>
              </Stack>
            </DialogFooter>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default UnitFormModal;
